import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import withData from '@bd/bdcore-data';
import { css } from '@bd/bdui-styles/emotion';
import { Link } from '@bd/bdcore-named-routes';
import { buildThumbnailUrl } from '@bd/bdcore-utilities';

import { typography, colors } from '../../../styles';

const styles = css`
  margin: 30px 0;
  @media(min-width: 992px) {
    margin: 120px 0;
  }
  .Areas_heading {
    padding: 20px 15px;
    display: flex;
    width: 100%;
    position: relative;
    justify-content: center;
    align-items: center;
    @media(min-width: 992px) {
      align-items: center;
      justify-content: flex-start;
      padding: 20px 80px;
      width: 33.333%;
      background-color: ${colors.neutral};
    }
    &:after {
      content: '';
      background-image: linear-gradient(20deg, rgba(255,255,255,0) 10%, rgba(255,255,255,.8) 40%);
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      transition: all .3s ease-out;
      opacity: 1;
      z-index: 1;
      display: none;
      @media(min-width: 992px) {
        display: block;
      }
    }
  }
  .Areas_headingTitle {
    font-family: ${typography.Serif};
    font-weight: 400;
    font-size: 36px;
    line-height: .7;
    text-align: center;
    margin: 0;
    position: relative;
    z-index: 2;
    @media(min-width: 480px) {
      font-size: 48px;
    }
    @media(min-width: 992px) {
      font-size: 75px;
      align-items: center;
      justify-content: center;
      text-align: left;
    }
  }
  .Areas_headingSubtitle {
    font-family: ${typography.Serif};
    font-weight: 400;
    font-size: 24px;
    letter-spacing: 0;
    line-height:30px;
    text-align: center;
    margin: 0;
    position: relative;
    z-index: 2;
    color: ${colors.brand2};
    @media(min-width: 480px) {
      font-size: 36px;
      line-height:42px;
    }
    @media(min-width: 992px) {
      align-items: center;
      text-align: left;
    }
    &::after {
      content: '';
      display: block;
      position: relative;
      margin: 20px auto 20px auto;
      width: 75px;
      height: 4px;
      background-color: ${colors.neutralDarkest};
      @media(min-width: 992px) {
        margin: 40px 0 0 0;
      }
    }
  }
  .Areas_list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    background-color: ${colors.neutral};
  }
  .Areas_listItem {
    overflow: hidden;
    width: 100%;
    position: relative;
    height: 100px;
    margin: 0 0 2px 0;
    @media(min-width: 992px) {
      width: 33.333%;
      height: 200px;
      margin: 0;
    }
  }
  .Areas_image {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
    transition: all 1s ease;
  }
  .Areas_link {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    z-index: 1;
    padding: 30px;
    @media(min-width: 992px) {
      font-size: 39px;
      letter-spacing: 0.54px;
      line-height: 35px;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    &:before {
      content: '';
      background-color: ${colors.brand4Lightest};
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      transition: all .3s ease-out;
      opacity: 1;
      z-index: 1;
    }
    &:after {
      content: '';
      background-image: linear-gradient(45deg, rgba(255,255,255,0) 18%, rgba(255,255,255, .8) 100%);
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      transition: all .3s ease-out;
      opacity: 1;
      z-index: 2;
    }
    &:hover, &:focus {
      color: ${colors.white};
      &:before {
        opacity: .8;
        background-color: ${colors.brand1};
      }
      &::after {
        opacity: 0;
      }
      .Areas_image {
        transform: scale(1.3);
      }
    }
  }
  .Areas_linkTitle {
    font-family: ${typography.Serif};
    font-weight: 700;
    font-size: 21px;
    text-align: center;
    line-height: 1;
    position: relative;
    z-index: 5;
    margin: 0;
    @media(min-width: 992px) {
      font-size: 28px;
      margin: 0 0 10px 0;
    }
  }
  .Areas_linkIcon {
    position: relative;
    z-index: 5;
    height: 40px;
    width: 40px;
    @media(min-width: 992px) {
      width: 50px;
      height: 50px;
    }
  }
`;

function AreasSection({ areas, content }) {
  return (
    <div className={styles}>
      <div>
        <div className="d-lg-flex">
          <div className="Areas_heading">
            <h3 className="Areas_headingTitle">
              {get(content, '[0].title')}&nbsp;<br />
              <span className="Areas_headingSubtitle">{get(content, '[0].subtitle')}</span>
            </h3>
          </div>
          <ul className="flex-fill Areas_list">
            {
              areas.filter(area => !area.isMetro).map(item => (
                <li className="Areas_listItem" key={item._id}>
                  <Link
                    className="Areas_link"
                    name="communities"
                    params={{
                      communityMarketAreaMetro: get(item, 'parentMetro'),
                      communityMarketAreaSubMetro: get(item, 'name'),
                    }}
                  >
                    <div className="Areas_linkTitle">{get(item, 'name')}</div>
                    <img className="Areas_linkIcon" src="/images/icon-circle-arrow-right-white.svg" alt="" />
                    <div
                      className="Areas_image"
                      alt={get(item, 'name')}
                      width={500}
                      height={500}
                      style={{ backgroundImage: `url(${buildThumbnailUrl(get(item, 'logo.contentUrl'), { width: 2000, height: 2000 })})` }}
                    />
                  </Link>
                </li>
              ))
            }
          </ul>
        </div>
      </div>
    </div>
  );
}

AreasSection.propTypes = {
  areas: PropTypes.arrayOf(PropTypes.object).isRequired,
  content: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default withData(
  {
    areas: true,
  },
)(AreasSection);



// WEBPACK FOOTER //
// ./src/components/PageLoader/Sections/AreasSection.section.js